export enum EnumProspectType {
  LEAD = 'LEAD',
  ACCOUNT = 'ACCOUNT',
  OTHER = 'OTHER'
}

// Shared constants for prospect primary key columns used across integrations (HubSpot, Salesforce, etc.)
export const PROSPECT_PRIMARY_KEY_COLUMNS = {
  WEBSITE_URL: 'website_url',
  LINKEDIN_URL: 'linkedin_url',
  DOMAIN: 'domain'
} as const;

export const PROSPECT_PREFIX = 'prospect.';

/**
 * Determines the primary key column name based on prospect type.
 * @param prospectType - The prospect type (LEAD, ACCOUNT, or OTHER)
 * @returns The primary key column name: 'linkedin_url' for LEAD, 'website_url' for ACCOUNT or OTHER
 */
export function determineProspectPrimaryKey(
  prospectType: EnumProspectType
): string {
  return prospectType === EnumProspectType.LEAD
    ? PROSPECT_PRIMARY_KEY_COLUMNS.LINKEDIN_URL
    : PROSPECT_PRIMARY_KEY_COLUMNS.WEBSITE_URL;
}

export enum EnumHotProspectStatus {
  NEW = 'new',
  HANDLED = 'handled'
}

export const DEFAULT_PAGE_SIZE = 100;
export const PROSPECT_QUERY_LIMIT = 200;
export const SEARCH_NEWS_QUERY_LIMIT = 20;

// ACCOUNTS
export interface AccountProspectSearchParams {
  last_fundraising_series: string[];
  industry: string[];
}

export interface AccountProspectQuery {
  industries: string[];
  countries?: string[] | null;
  size_ranges?: string[] | null;
  last_fundraising_date?: string | null;
  last_fundraising_series?: string[] | null;
}

export interface AccountProspectScrapeQuery {
  website_url: string;
  prompt: string;
}

export interface ScrapeJobRequest {
  website_url: string;
  target_property: string[];
}

export interface ScrapeJobResponse {
  message: string;
  job_id: string;
  status: string;
}

export interface ScrapeJobStatus {
  job_id: string;
  status: string;
  progress?: number;
  error?: string;
}

// Background job types for account prospect search
export interface AccountSearchJobResponse {
  message: string;
  job_id: string;
  status: string;
}

export interface AccountSearchJobStatus {
  job_id: string;
  status: string;
  error?: string;
}

export interface AccountProspectResponse {
  id: string;
  domain: string;
  name: string;
  location: string;
  size: string;
  industry: string;
  linkedin_url: string;
  extra_data?: {
    hot_score?: number;
    hot_reason?: string;
    status?: EnumHotProspectStatus;
    [key: string]: any;
  };
}

export interface AccountProspectDetailResponse extends AccountProspectResponse {
  workflows_output: Record<string, any>;
}

export interface AccountProspectCreate {
  domain: string;
  name: string;
  size?: string;
  industry?: string;
  location?: string;
  linkedin_url?: string;
  extra_data?: Record<string, any>;
}

export const AccountProspectRequiredField = ['domain', 'name'];

// LEADS
export interface LeadProspectSearchField {
  type: string;
  label: string;
  description: string;
  placeholder: string;
}

export interface LeadProspectSearchParams {
  position: LeadProspectSearchField;
  location: LeadProspectSearchField;
}

export interface LeadProspectQuery {
  positions: string[]; // Array of position titles
  location: string; // Single location string
}

export interface LeadProspectResponse {
  id: string;
  linkedin_url: string;
  first_name: string;
  last_name: string;
  emails: string[];
  phone_numbers: string[];
  current_company_id: string;
  extra_data?: {
    hot_score?: number;
    hot_reason?: string;
    status?: EnumHotProspectStatus;
  };
}

export interface LeadSearchResult {
  linkedin_url: string;
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  location?: string | null;
}

export interface LeadSearchJobResponse {
  message: string;
  job_id: string;
  status: string;
}

export interface LeadSearchJobStatus {
  job_id: string;
  status: string;
  error?: string;
}

export interface LeadProspectDetailResponse extends LeadProspectResponse {
  workflows_output: Record<string, any>;
}

export interface LeadProspectCreate {
  linkedin_url: string;
  first_name: string;
  last_name: string;
  emails?: string[];
  phone_numbers?: string[];
  current_company_id?: string;
}

export const LeadProspectRequiredField = [
  'linkedin_url',
  'first_name',
  'last_name'
];

// News Search
export interface NewsSearchRequest {
  keyword: string; // 1..50 chars
  filter_date: string; // 'YYYY-MM'
  limit: number; // 1..200
}

export interface NewsResult {
  company_name: string;
  domain: string; // e.g. "https://www.transunion.com"
  ticker: string; // e.g. "TRU" or "N/A"
  date: string; // ISO date 'YYYY-MM-DD'
  summary: string;
}

export interface NewsApiResponse {
  results: NewsResult[];
}

// REGISTRIES
export enum EnumRegistryType {
  CYBER_ATTACKS = 'CYBER_ATTACKS',
  IPO_FILINGS = 'IPO_FILINGS',
  M_AND_A = 'M_AND_A'
}

export interface RegistryProspectQuery {
  registry_type: EnumRegistryType;
  month: string;
  limit: number;
}

export interface RegistryJobCreateResponse {
  message?: string;
  status: 'queued' | 'empty' | string;
  job_id?: string | null;
  queue_position?: number;
  found_sec_count?: number;
}

export interface RegistryJobStatusResponse {
  job_id: string;
  status: 'queued' | 'started' | 'finished' | 'failed' | string;
  created_at?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  result?: unknown;
  error?: string;
}

export interface RegistryJobResultResponse {
  status: 'success' | 'error';
  data?: AccountProspectResponse[];
  total_count?: number;
  error?: string;
  month?: string;
  types?: string[];
  limit?: number;
}

export interface HotProspect {
  id: string;
  prospect_type: EnumProspectType;
  name: string;
  prospect_url: string;
  subtitle: string;
  location: string | null;
  hot_score: number;
  hot_reason: string;
  status: string;
}

export interface HotProspectsListResponse {
  prospects: HotProspect[];
  total_count: number;
  skip: number;
  limit: number;
}
